import ClearableTextField from '@/components/ClearableTextField';
import DropdownList from '@/components/DropdownList';
import LocationSearchAutocomplete from '@/components/LocationSearchAutocomplete';
import DatePickers from '@/modules/JobSearchFilters/components/DatePickers/DatePickers';
import MinPayRateDropdown from '@/modules/JobSearchFilters/components/MinPayRateDropdown/MinPayRateDropdown';
import WorkTypeButtonGroup from '@/modules/JobSearchFilters/components/WorkTypeButtonGroup/WorkTypeButtonGroup';
import { ILocation, ISchedulerLocation } from '@/services/types';
import {
  DateSearch,
  JobRootSpecialtyV2,
  JobSearchGrade,
  JobSpeciality,
  JobSubSpeciality,
  MinPayRateUnit,
  WorkType,
} from '@/types/jobs';
import { clearScrollWhenSearchCriteriaHasChanged } from '@/utils/jobSearchAutoScroll';
import { getRadiusOptions } from '@/utils/jobSearchBoxOptions';
import debounce from 'lodash.debounce';
import React, { ChangeEvent, ReactElement, useEffect, useState } from 'react';

import { Button, Collapse, FormControl, Theme, useMediaQuery } from '@material-ui/core';
import FilterListOutlinedIcon from '@material-ui/icons/FilterListOutlined';
import KeyboardArrowDownOutlinedIcon from '@material-ui/icons/KeyboardArrowDownOutlined';
import KeyboardArrowUpOutlinedIcon from '@material-ui/icons/KeyboardArrowUpOutlined';

import styles from './JobSearchFiltersForm.module.scss';

export type JobSearchFiltersFormProps = {
  date: DateSearch;
  onDateChange: (value: DateSearch) => void;
  grade?: string;
  onGradeChange: (value: JobSearchGrade) => void;
  speciality?: string;
  onSpecialityChange: (value: JobSpeciality) => void;
  rootSpeciality?: string;
  onRootSpecialityChange: (value: JobRootSpecialtyV2) => void;
  subspeciality?: string;
  onSubSpecialityChange: (value: JobSubSpeciality) => void;
  keyword?: string;
  onKeywordChange: (value: string) => void;
  workType?: WorkType;
  onWorkTypeChange: (value: WorkType | undefined) => void;
  locationCoordinate: ILocation | ISchedulerLocation | null;
  onLocationCoordinateChange: (value: ILocation | ISchedulerLocation | null) => void;
  radius?: string;
  onRadiusChange: (value: string) => void;
  minPayRate: string | null;
  onMinPayRateChange: (value: string | null) => void;
  minPayRateUnit: MinPayRateUnit;
  onMinPayRateUnitChange: (value: MinPayRateUnit) => void;
  isEndDateDisabled: boolean;
  isRadiusDisabled: boolean;
  onClear: () => void;
  getGradeOptions: () => ReactElement[];
  getSpecialityOptions: () => ReactElement[];
  getRootSpecialityOptions: () => ReactElement[];
  getSubSpecialityOptions: () => ReactElement[];
};

const JobSearchFiltersForm = ({
  date,
  onDateChange,
  grade,
  onGradeChange,
  speciality,
  onSpecialityChange,
  keyword,
  onKeywordChange,
  workType,
  onWorkTypeChange,
  radius,
  onRadiusChange,
  locationCoordinate,
  onLocationCoordinateChange,
  minPayRate,
  onMinPayRateChange,
  minPayRateUnit,
  onMinPayRateUnitChange,
  isEndDateDisabled,
  isRadiusDisabled,
  onClear,
  getGradeOptions,
  getSpecialityOptions,
  subspeciality,
  onSubSpecialityChange,
  getSubSpecialityOptions,
  rootSpeciality,
  onRootSpecialityChange,
  getRootSpecialityOptions,
}: JobSearchFiltersFormProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const isCollapsible = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  // by default, should be closed on mobile and opened otherwise
  useEffect(() => {
    setIsOpen(isCollapsible === false);
  }, [isCollapsible]);

  const handleOpenClick = () => {
    if (isCollapsible) {
      setIsOpen(!isOpen);
    }
  };

  const handleKeywordChange = debounce(
    (e: ChangeEvent<HTMLInputElement>) => onKeywordChange(e.target.value),
    500,
  );

  return (
    <div data-testid={'job-filters-container'} className={styles.container}>
      <button
        className={styles.header}
        onClick={handleOpenClick}
        disabled={isCollapsible === false}
      >
        <FilterListOutlinedIcon />
        <span className={styles.header_text}>Filters</span>
        {isCollapsible && (
          <span className={styles.header_downIcon}>
            {isOpen ? <KeyboardArrowUpOutlinedIcon /> : <KeyboardArrowDownOutlinedIcon />}
          </span>
        )}
      </button>
      <Collapse in={isOpen} timeout="auto">
        <div className={styles.body}>
          <div>
            <DropdownList
              id="grade"
              label="Grade"
              value={grade}
              testId="job-search-grade-input"
              onChange={e => {
                onGradeChange(('' + e.target.value) as JobSearchGrade);
                clearScrollWhenSearchCriteriaHasChanged();
              }}
              renderOptions={getGradeOptions}
            />
          </div>

          <div>
            <DropdownList
              id="rootSpeciality"
              label="Speciality"
              value={rootSpeciality}
              testId="job-search-root-speciality-input"
              onChange={e => {
                onRootSpecialityChange(('' + e.target.value) as JobRootSpecialtyV2);
                clearScrollWhenSearchCriteriaHasChanged();
              }}
              renderOptions={getRootSpecialityOptions}
            />
          </div>
          <div>
            <DropdownList
              id="subspeciality"
              label="Subspeciality"
              value={subspeciality}
              testId="job-search-sub-speciality-input"
              onChange={e => {
                onSubSpecialityChange(('' + e.target.value) as JobSubSpeciality);
                clearScrollWhenSearchCriteriaHasChanged();
              }}
              renderOptions={getSubSpecialityOptions}
            />
          </div>

          <div className={styles.workType}>
            <WorkTypeButtonGroup
              selectedWorkType={workType}
              onWorkTypeChange={workType => {
                onWorkTypeChange(workType);
                clearScrollWhenSearchCriteriaHasChanged();
              }}
            />
          </div>

          <div>
            <LocationSearchAutocomplete
              selectedLocation={locationCoordinate}
              onSelectedLocationChange={onLocationCoordinateChange}
            />
          </div>

          <div>
            <DropdownList
              id="radius"
              label="Radius"
              value={radius}
              testId="job-search-radius-input"
              disabled={isRadiusDisabled}
              tooltipMessage={
                isRadiusDisabled
                  ? 'Radius is available when a specific city or town is selected.'
                  : undefined
              }
              onChange={e => {
                onRadiusChange(e.target.value);
                clearScrollWhenSearchCriteriaHasChanged();
              }}
              renderOptions={() => getRadiusOptions()}
            />
          </div>

          <div className={styles.dates}>
            <DatePickers
              date={date}
              onDateChange={onDateChange}
              isEndDateDisabled={isEndDateDisabled}
            />
          </div>

          <div>
            <FormControl fullWidth>
              <ClearableTextField
                id="keywords"
                name="keywords"
                placeholder="Keywords"
                label={'Keywords'}
                defaultValue={keyword ?? ''}
                onChange={handleKeywordChange}
                inputProps={{ 'data-testid': 'job-search-keywords-input' }}
              />
            </FormControl>
          </div>

          <div>
            <MinPayRateDropdown
              minPayRate={minPayRate}
              onMinPayRateChange={onMinPayRateChange}
              minPayRateUnit={minPayRateUnit}
              onMinPayRateUnitChange={onMinPayRateUnitChange}
            />
          </div>

          <div className={styles.clear}>
            <Button
              type="button"
              onClick={onClear}
              color="primary"
              size="medium"
              variant="text"
              data-testid="job-search-clear"
              className={styles.clearButton}
            >
              Clear all
            </Button>
          </div>
        </div>
      </Collapse>
    </div>
  );
};

export default JobSearchFiltersForm;
